import type { Document } from '../types';
import { authService } from './authService';

const resolveSessionDocumentApiBase = () => {
  if (import.meta.env.VITE_GROUNDING_API_BASE) {
    return import.meta.env.VITE_GROUNDING_API_BASE;
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    if (hostname === 'rrrealty.ai' || hostname === 'www.rrrealty.ai') {
      return 'https://fn-conversationsave.azurewebsites.net/api';
    }

    if (hostname === 'testing.rrrealty.ai') {
      return 'https://fn-conversationsave.azurewebsites.net/api';
    }

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:7071/api';
    }
  }

  return 'https://fn-conversationsave.azurewebsites.net/api';
};

const SESSION_DOCUMENT_API_BASE = resolveSessionDocumentApiBase();
const SESSION_DOCUMENTS_STORAGE_KEY = 'rai_session_documents';

interface SessionDocumentResponse {
  success: boolean;
  document?: {
    id?: string;
    documentId?: string;
    conversationId: string;
    fileName: string;
    originalFileName?: string;
    contentType: string;
    fileSize: number;
    status?: Document['status'];
    kind?: Document['kind'];
    uploadDate?: string;
    expiresAt?: string;
    summary?: Document['summary'];
  };
  error?: string;
  message?: string;
}

function mapDocument(raw: NonNullable<SessionDocumentResponse['document']>): Document {
  const id = raw.documentId || raw.id || '';
  return {
    id,
    fileName: raw.fileName,
    originalFileName: raw.originalFileName || raw.fileName,
    contentType: raw.contentType,
    fileSize: raw.fileSize,
    uploadDate: raw.uploadDate ? new Date(raw.uploadDate) : new Date(),
    userId: 'current-user',
    conversationId: raw.conversationId,
    sessionDocument: true,
    status: raw.status,
    kind: raw.kind,
    expiresAt: raw.expiresAt,
    summary: raw.summary,
  };
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  return window.sessionStorage;
}

function readStoredDocuments(): Document[] {
  const storage = getSessionStorage();
  if (!storage) return [];

  try {
    const raw = storage.getItem(SESSION_DOCUMENTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item.id === 'string')
      .map((item) => ({
        ...item,
        uploadDate: item.uploadDate ? new Date(item.uploadDate) : new Date(),
        sessionDocument: true,
      }));
  } catch {
    storage.removeItem(SESSION_DOCUMENTS_STORAGE_KEY);
    return [];
  }
}

function writeStoredDocuments(documents: Document[]): void {
  const storage = getSessionStorage();
  if (!storage) return;
  storage.setItem(SESSION_DOCUMENTS_STORAGE_KEY, JSON.stringify(documents));
}

function rememberDocument(document: Document): void {
  const documents = readStoredDocuments();
  const next = [
    ...documents.filter((item) => item.id !== document.id),
    document,
  ];
  writeStoredDocuments(next);
}

function forgetDocument(documentId: string): void {
  writeStoredDocuments(readStoredDocuments().filter((document) => document.id !== documentId));
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await authService.getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

class SessionDocumentService {
  async getDocuments(conversationId?: string): Promise<Document[]> {
    const documents = readStoredDocuments();
    return conversationId
      ? documents.filter((document) => document.conversationId === conversationId)
      : documents;
  }

  async uploadDocument(file: File, conversationId: string): Promise<Document> {
    const form = new FormData();
    form.append('conversationId', conversationId);
    form.append('file', file);

    const response = await fetch(`${SESSION_DOCUMENT_API_BASE}/sessiondocuments/upload`, {
      method: 'POST',
      headers: await authHeaders(),
      body: form,
    });

    const payload = await response.json() as SessionDocumentResponse;
    if (!response.ok || !payload.success || !payload.document) {
      throw new Error(payload.message || payload.error || `Session document upload failed (${response.status})`);
    }

    const document = mapDocument(payload.document);
    rememberDocument(document);
    return document;
  }

  async getStatus(conversationId: string, documentId: string): Promise<Document> {
    const params = new URLSearchParams({ conversationId, documentId });
    const response = await fetch(`${SESSION_DOCUMENT_API_BASE}/sessiondocuments/status?${params.toString()}`, {
      headers: await authHeaders(),
    });

    const payload = await response.json() as SessionDocumentResponse;
    if (!response.ok || !payload.success || !payload.document) {
      throw new Error(payload.message || payload.error || `Session document status failed (${response.status})`);
    }

    const document = mapDocument(payload.document);
    rememberDocument(document);
    return document;
  }

  async deleteDocument(documentId: string, conversationId: string): Promise<void> {
    const params = new URLSearchParams({ conversationId });
    const response = await fetch(`${SESSION_DOCUMENT_API_BASE}/sessiondocuments/${encodeURIComponent(documentId)}?${params.toString()}`, {
      method: 'DELETE',
      headers: await authHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Session document delete failed (${response.status})`);
    }
    forgetDocument(documentId);
  }
}

export const sessionDocumentService = new SessionDocumentService();
